import { useMemo } from "react";
import { formatCurrency } from "@/lib/currency";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  applyBounds,
  arePricingRulesActive,
  computeServiceTotal,
  normalizePricingRules,
  type PricingRulesV1,
} from "@/lib/pricingRules";
import type { BookingPolicyV2 } from "@/lib/bookingPolicy";

interface PriceBreakdownAccordionProps {
  basePrice: number;
  sizeSqm: number;
  /** @deprecated kept for backwards compatibility but no longer used:
   *  urgency is resolved from resolvedBookingMode + bookingPolicy only. */
  urgency?: "normal" | "urgent" | "same_day";
  serviceType: string;
  pricingRules: PricingRulesV1 | null | undefined;
  pricingType?: string | null;
  discountAmount?: number;
  customerPaymentFee?: number;
  resolvedBookingMode?: "instant" | "request" | "urgent" | null;
  bookingPolicy?: BookingPolicyV2 | null;
  /** Number of occurrences when the booking is part of a recurring series.
   *  Defaults to 1 (single booking). When > 1, the breakdown shows the
   *  per-visit price plus a series total = perVisit * occurrences. */
  occurrences?: number;
}

export default function PriceBreakdownAccordion({
  basePrice,
  sizeSqm,
  serviceType,
  pricingRules,
  pricingType,
  discountAmount = 0,
  customerPaymentFee = 0,
  resolvedBookingMode,
  bookingPolicy,
  occurrences = 1,
}: PriceBreakdownAccordionProps) {
  const {
    sizeAdj,
    urgencyAdj,
    typeAdj,
    total,
    confidence,
    boundsMin,
    boundsMax,
    clamped,
    modifiersActive,
  } = useMemo(() => {
    const rr = normalizePricingRules(pricingRules || {});
    const pol = bookingPolicy || {};
    const active = arePricingRulesActive(rr, pricingType);
    const isUrgent = resolvedBookingMode === "urgent";
    const urgMult = isUrgent
      ? (Number(pol?.urgent?.pricing_value) || 1.5)
      : 1;

    const result = computeServiceTotal({
      basePrice,
      rules: rr,
      pricingType,
      sqm: active ? sizeSqm : undefined,
      urgencyMultiplier: urgMult,
      serviceTypeKey: active ? serviceType : undefined,
    });

    const sizeAdjVal = active ? Math.round(basePrice * (result.sizeMultiplier - 1)) : 0;
    const typeAdjVal = active
      ? Math.round(basePrice * result.sizeMultiplier * (result.typeMultiplier - 1))
      : 0;
    // Urgency surcharge is computed AFTER bounds clamping (CF19).
    // The accordion delta must match the engine: clamped line * (urgMult-1).
    const lineBeforeUrgency = active
      ? applyBounds(Math.round(basePrice * result.sizeMultiplier * result.typeMultiplier), rr.bounds)
      : Math.round(basePrice);
    const urgencyAdjVal = Math.round(lineBeforeUrgency * (result.urgencyMultiplier - 1));

    return {
      sizeAdj: sizeAdjVal,
      urgencyAdj: urgencyAdjVal,
      typeAdj: typeAdjVal,
      total: result.total,
      confidence: 100,
      boundsMin: rr.bounds?.min,
      boundsMax: rr.bounds?.max,
      clamped: result.clamped,
      modifiersActive: active,
    };
  }, [basePrice, pricingRules, pricingType, serviceType, sizeSqm, resolvedBookingMode, bookingPolicy]);

  const occ = Math.max(1, Math.floor(Number(occurrences) || 1));
  const isRecurring = occ > 1;
  const discount = Math.max(0, Math.round(discountAmount || 0));
  const perVisitTotal = Math.max(0, total - discount);
  const seriesTotal = perVisitTotal * occ;
  const fee = Math.max(0, Math.round((customerPaymentFee || 0) * 100) / 100);
  const grandTotal = seriesTotal + fee;
  const showBoundsHint =
    modifiersActive && (typeof boundsMin === "number" || typeof boundsMax === "number");

  return (
    <Card className="border-gray-200 shadow-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-gray-900">Dettaglio prezzo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">Servizio base</span>
          <span className="text-gray-900">{formatCurrency(basePrice)}</span>
        </div>
        {modifiersActive && sizeAdj !== 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">Dimensione ({sizeSqm} m2)</span>
            <span className="text-gray-900">
              {sizeAdj >= 0 ? "+" : "-"}{formatCurrency(Math.abs(sizeAdj))}
            </span>
          </div>
        )}
        {urgencyAdj > 0 && resolvedBookingMode === "urgent" && (
          <div className="flex justify-between">
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-gray-600 flex items-center gap-1 cursor-help">
                    Tempistica ravvicinata
                    <Info className="h-3.5 w-3.5 text-gray-400" aria-label="info" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-left">
                  Hai scelto una data/ora che ricade nella fascia urgente concordata con il fornitore
                  (tipicamente entro 12-24 ore). Il sistema applica automaticamente la maggiorazione
                  in base alla data: non viene chiesto separatamente.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-gray-900">+{formatCurrency(urgencyAdj)}</span>
          </div>
        )}
        {modifiersActive && typeAdj !== 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">Tipologia</span>
            <span className="text-gray-900">
              {typeAdj >= 0 ? "+" : "-"}{formatCurrency(Math.abs(typeAdj))}
            </span>
          </div>
        )}
        {clamped === "min" && (
          <div className="text-xs text-gray-500 italic">
            Prezzo allineato al minimo concordato con il fornitore.
          </div>
        )}
        {clamped === "max" && (
          <div className="text-xs text-gray-500 italic">
            Prezzo allineato al massimo concordato con il fornitore.
          </div>
        )}
        {discount > 0 && (
          <div className="flex justify-between text-green-700">
            <span className="text-gray-600">Sconto stagionale</span>
            <span>-{formatCurrency(discount)}</span>
          </div>
        )}
        {fee > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">Fee di pagamento/piattaforma</span>
            <span className="text-gray-900">{formatCurrency(fee)}</span>
          </div>
        )}
        {showBoundsHint && (
          <div className="text-xs text-gray-500">
            Range concordato con il fornitore:{" "}
            {typeof boundsMin === "number" ? formatCurrency(boundsMin) : "-"}
            {" - "}
            {typeof boundsMax === "number" ? formatCurrency(boundsMax) : "-"}
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="text-gray-600 flex items-center gap-2">
            Affidabilita
            <Progress value={confidence} className="mt-1 h-2 w-24" />
            <div className="text-xs text-gray-500">{confidence}%</div>
          </div>
          <span className="text-gray-900"></span>
        </div>

        <Separator className="my-2" />

        {isRecurring && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Per visita</span>
              <span className="text-gray-900">{formatCurrency(perVisitTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Occorrenze</span>
              <span className="text-gray-900">{occ}</span>
            </div>
          </>
        )}
        <div className="flex justify-between font-semibold">
          <span className="text-gray-900">
            {isRecurring
              ? (fee > 0 ? "Totale serie dovuto oggi" : "Totale serie")
              : (fee > 0 ? "Totale dovuto oggi" : "Totale servizio")}
          </span>
          <span className="text-lg text-primary">{formatCurrency(grandTotal)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
